import React, { useState } from "react"
import api from "../services/api"
import Swal from "sweetalert2"
import { useNavigate } from "react-router-dom"
import { auth } from "../auth/auth"

export default function Register() {
  const navigate = useNavigate()

  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState({})
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
    role: "admin",
  })

  const validate = () => {
    const newErrors = {}

    if (!formData.name.trim()) newErrors.name = "Name is required"
    if (!formData.email.trim()) newErrors.email = "Email is required"
    if (!formData.password.trim()) newErrors.password = "Password is required"
    if (formData.password.length < 6)
      newErrors.password = "Password must be at least 6 characters"

    if (!formData.confirmPassword.trim())
      newErrors.confirmPassword = "Confirm password is required"

    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match"
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))

    setErrors((prev) => {
      if (!prev[name]) return prev
      const { [name]: _, ...rest } = prev
      return rest
    })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!validate()) return

    setLoading(true)

    Swal.fire({
      title: "Creating account...",
      text: "Please wait",
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => Swal.showLoading(),
    })

    try {
      const res = await api.post(`/api/auth/register`, {
        name: formData.name,
        email: formData.email,
        password: formData.password,
        role: formData.role,
      })

      // backend returns: { token, user }
      const token = res.data?.token
      const user = res.data?.user

      if (token) auth.setToken(token)
      if (user) auth.setUser(user)

      Swal.close()
      Swal.fire({
        icon: "success",
        title: "Registered!",
        text: "Account created successfully.",
        confirmButtonColor: "#f97316",
        timer: 1200,
        showConfirmButton: false,
      })

      navigate("/units", { replace: true })
    } catch (err) {
      Swal.close()
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        "Registration failed. Please try again."

      Swal.fire({
        icon: "error",
        title: "Register failed",
        text: msg,
        confirmButtonColor: "#f97316",
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <div
      className="min-h-screen bg-gray-50 flex items-center justify-center p-6"
      style={{
        backgroundImage:
          "url('https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?auto=format&fit=crop&w=1920&q=80')",
      }}
    >
      <div className="absolute inset-0 bg-black/50" />
      <div className="relative w-full max-w-md rounded-xl border border-white/20 bg-white/90 backdrop-blur shadow-2xl p-6 animate-fadeIn">
        <h1 className="text-2xl font-bold mb-2">Create Account</h1>
        <p className="pb-5 text-gray-600 italic">
          Register an admin/staff account.
        </p>

        <form onSubmit={handleSubmit} className="space-y-4" autoComplete="off">
          {/* Name */}
          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">Name</label>
            <input
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Juan Dela Cruz"
              className={`w-full rounded-lg border px-3 py-2 text-sm
                focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500
                transition ${errors.name ? "border-red-500" : "border-gray-300"}`}
            />
            {errors.name && (
              <p className="text-xs text-red-500">{errors.name}</p>
            )}
          </div>

          {/* Email */}
          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">Email</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="admin@email.com"
              className={`w-full rounded-lg border px-3 py-2 text-sm
                focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500
                transition ${errors.email ? "border-red-500" : "border-gray-300"}`}
            />
            {errors.email && (
              <p className="text-xs text-red-500">{errors.email}</p>
            )}
          </div>

          {/* Role */}
          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">Role</label>
            <select
              name="role"
              value={formData.role}
              onChange={handleChange}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm
                focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition"
            >
              <option value="admin">admin</option>
              <option value="staff">staff</option>
            </select>
          </div>

          {/* Password */}
          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">
              Password
            </label>
            <input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="••••••••"
              className={`w-full rounded-lg border px-3 py-2 text-sm
                focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500
                transition ${errors.password ? "border-red-500" : "border-gray-300"}`}
            />
            {errors.password && (
              <p className="text-xs text-red-500">{errors.password}</p>
            )}
          </div>

          {/* Confirm Password */}
          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">
              Confirm Password
            </label>
            <input
              type="password"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              placeholder="••••••••"
              className={`w-full rounded-lg border px-3 py-2 text-sm
                focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500
                transition ${
                  errors.confirmPassword ? "border-red-500" : "border-gray-300"
                }`}
            />
            {errors.confirmPassword && (
              <p className="text-xs text-red-500">{errors.confirmPassword}</p>
            )}
          </div>

          <button
            type="submit"
            disabled={loading}
            className={`w-full px-4 py-2 rounded-lg text-sm font-semibold
              bg-orange-600 text-white hover:bg-orange-700
              focus:ring-2 focus:ring-orange-500 focus:ring-offset-1
              transition ${loading ? "opacity-70 cursor-not-allowed" : ""}`}
          >
            {loading ? "Creating..." : "Register"}
          </button>

          <button
            type="button"
            onClick={() => navigate("/login")}
            className="w-full px-4 py-2 rounded-lg text-sm font-semibold
              border border-gray-300 text-gray-700 hover:bg-gray-100 transition"
          >
            Back to Login
          </button>
        </form>
      </div>
    </div>
  )
}
